import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Pencil, Briefcase, Bell, User as UserIcon, Phone, Megaphone, Home, Mail,
  MapPin, Calendar, Check, ChevronRight,
} from "lucide-react";

const TABS = [
  { key: "perso",  label: "Informations personnelles",   step: 0 },
  { key: "pro",    label: "Informations professionnelles", step: 1 },
  { key: "skills", label: "Compétences & logiciels",     step: 2 },
];

const SOFTWARES_REF = ["Winpharma", "LGPI", "Smart RX", "Pharmaland", "Leo",
  "Fiducial Pharma", "Alliance Premium", "Pharmagest", "Caduciel",
  "ProPharmaX", "Tactil", "TriaPharm", "pharma4", "pharmacie.cloud"];
const MISSIONS_REF = ["Vaccination", "Tests de dépistage (TROD angine)", "Tests de dépistage (TROD cystite)",
  "Prescription", "Entretiens pharmaceutiques",
  "Tests allergies", "Tests cholestérol", "Otoscopie", "Test glycémie"];

export default function MyProfile() {
  const { user } = useAuth();
  const nav = useNavigate();

  const avatarSrc = user?.avatar
    ? (user.avatar.startsWith("http") ? user.avatar : `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`)
    : null;
  const displayName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "—";
  const initial = (user?.first_name?.[0] || user?.name?.[0] || "?").toUpperCase();

  const goEdit = (step = 0) => nav(`/profile/edit?step=${step}`);
  const scrollTo = (key) => {
    const el = document.getElementById(`section-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-w-0">
      <div className="max-w-7xl mx-auto p-4 lg:p-8 pb-24 lg:pb-10">
          {/* Top bar */}
          <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Mon profil</h1>
              <p className="text-sm text-[#6B726A] mt-1">Vos informations sont visibles des recruteurs pour vous proposer des missions adaptées.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => goEdit(0)} data-testid="edit-profile-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143C24]">
                <Pencil className="w-4 h-4" /> Modifier mon profil
              </button>
              <button data-testid="bell-btn"
                className="w-10 h-10 rounded-full bg-white border border-[#E2E5E0] grid place-items-center text-[#1A4D2E] hover:bg-[#F7F8F6]">
                <Bell className="w-4 h-4" />
              </button>
              <div className="w-10 h-10 rounded-full overflow-hidden bg-[#E8F0EA] grid place-items-center text-[#1A4D2E] font-medium" data-testid="top-avatar">
                {avatarSrc ? <img src={avatarSrc} alt={displayName} className="w-full h-full object-cover" /> : initial}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-[#E2E5E0] p-2 mb-6 overflow-x-auto" data-testid="profile-tabs">
            <div className="flex items-center gap-1 min-w-max">
              {TABS.map((tab) => (
                <button key={tab.key} onClick={() => scrollTo(tab.key)} data-testid={`tab-${tab.key}`}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-[#3F4640] hover:bg-[#F7F8F6] border-b-2 border-transparent hover:border-[#1A4D2E]/30 transition-colors">
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            {/* Left main column */}
            <div className="space-y-6 min-w-0">
              {/* SECTION 1 — Informations personnelles */}
              <SectionCard id="section-perso" title="Informations personnelles" onEdit={() => goEdit(0)} testid="card-perso">
                <KVGrid>
                  <KV icon={UserIcon} label="Civilité" value={user?.civility} />
                  <KV icon={Megaphone} label="Comment nous avez-vous connu ?" value={user?.referral_source} />
                  <KV icon={UserIcon} label="Prénom" value={user?.first_name} />
                  <KV icon={Home} label="Adresse" value={user?.address} valueFallback="Adresse non renseignée" />
                  <KV icon={UserIcon} label="Nom" value={user?.last_name} />
                  <KV icon={Calendar} label="Code postal" value={user?.postal_code} />
                  <KV icon={Mail} label="Email" value={user?.email} />
                  <KV icon={MapPin} label="Ville" value={user?.city} />
                  <KV icon={Phone} label="Téléphone" value={formatPhone(user)} />
                </KVGrid>
              </SectionCard>

              {/* SECTION 2 — Informations professionnelles */}
              <SectionCard id="section-pro" title="Informations professionnelles" onEdit={() => goEdit(1)} testid="card-pro">
                <KVGrid>
                  <KV icon={UserIcon} label="Profil" value={user?.professional_role} />
                  <KV icon={Calendar} label="Travail en officine (12 derniers mois)" value={user?.recent_officine_experience} />
                  <KV icon={Briefcase} label="Expériences en officine" value={user?.recent_officine_experience} />
                  <KV icon={Calendar} label="Dates essentielles" value={user?.recent_experience_dates} />
                </KVGrid>
                <div className="mt-6 pt-5 border-t border-dashed border-[#E2E5E0]" data-testid="mobility-block">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-3">Mobilité géographique</div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F7F8F6] border border-[#E2E5E0] text-sm font-medium text-[#0F2A1B]" data-testid="mobility-distance">
                      <MapPin className="w-4 h-4 text-[#1A4D2E]" />
                      {user?.max_distance_km ? `${user.max_distance_km} km autour de ${user?.city || "votre position"}` : "Non renseigné"}
                    </div>
                    <CheckRow label="Véhicule personnel" on={!!user?.vehicle} />
                  </div>
                  {(user?.mobility_countries || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mobility-countries">
                      {user.mobility_countries.map((c) => (
                        <span key={c} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] bg-[#F7F8F6] border border-[#E2E5E0] text-[#0F2A1B]">
                          <img src={`https://flagcdn.com/w20/${c.toLowerCase()}.png`} alt="" className="w-4 h-auto rounded-[2px]" />
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-[#6B726A] mt-2">Rayon appliqué autour de votre position, dans les pays sélectionnés (UE + Suisse).</p>
                </div>
              </SectionCard>

              {/* SECTION 3 — Compétences & logiciels */}
              <SectionCard id="section-skills" title="Compétences & logiciels" onEdit={() => goEdit(2)} testid="card-skills">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-3">Logiciels maîtrisés</div>
                    <div className="grid grid-cols-2 gap-y-2">
                      {SOFTWARES_REF.map((s) => {
                        const on = (user?.softwares || []).includes(s);
                        return (
                          <div key={s} className="flex items-center gap-2 text-sm" data-testid={`soft-${s}`}>
                            <span className={`w-4 h-4 rounded-md grid place-items-center ${on ? "bg-[#1A4D2E] text-white" : "bg-[#F0F2EF] text-transparent"}`}>
                              <Check className="w-3 h-3" />
                            </span>
                            <span className={on ? "text-[#0F2A1B]" : "text-[#9AA09A]"}>{s}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-3">Nouvelles missions du pharmacien</div>
                    <div className="space-y-2">
                      {MISSIONS_REF.map((m) => {
                        const on = (user?.new_missions || []).includes(m);
                        return (
                          <div key={m} className="flex items-center gap-2 text-sm" data-testid={`mission-${m}`}>
                            <span className={`w-4 h-4 rounded-md grid place-items-center ${on ? "bg-[#1A4D2E] text-white" : "bg-[#F0F2EF] text-transparent"}`}>
                              <Check className="w-3 h-3" />
                            </span>
                            <span className={on ? "text-[#0F2A1B]" : "text-[#9AA09A]"}>{m}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {user?.itinerant_replacement && (
                  <div className="mt-6 pt-5 border-t border-dashed border-[#E2E5E0]">
                    <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-1">Communauté de remplaçants itinérants («&nbsp;sac à dos&nbsp;»)</div>
                    <div className="text-sm text-[#0F2A1B]">{user.itinerant_replacement}</div>
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Right summary column */}
            <aside className="space-y-4 lg:sticky lg:top-6 self-start" data-testid="profile-summary">
              <div className="bg-white rounded-2xl border border-[#E2E5E0] p-6">
                <h3 className="text-base font-medium mb-4 text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Profil en résumé</h3>
                <SummaryRow label="Profil" value={user?.professional_role} />
                <SummaryRow label="Expérience en officine" value={user?.recent_experience_dates ? `Oui, depuis ${user.recent_experience_dates}` : (user?.recent_officine_experience || "—")} />
                <SummaryRow label="Zone de recherche" value={user?.max_distance_km ? `Jusqu'à ${user.max_distance_km} km autour de ${user?.city || "votre position"}${user?.mobility_countries?.length ? ` · ${user.mobility_countries.join(", ")}` : ""}` : (user?.city || "—")} last />
              </div>
            </aside>
          </div>
        </div>
    </div>
  );
}

function SectionCard({ id, title, onEdit, testid, children }) {
  return (
    <section id={id} className="bg-white rounded-2xl border border-[#E2E5E0] p-6 sm:p-8" data-testid={testid}>
      <div className="flex items-center justify-between mb-5 gap-3">
        <h2 className="text-lg font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>{title}</h2>
        <button onClick={onEdit} data-testid={`${testid}-edit`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E2E5E0] text-xs font-medium text-[#1A4D2E] hover:bg-[#F7F8F6]">
          <Pencil className="w-3.5 h-3.5" /> Modifier
        </button>
      </div>
      {children}
    </section>
  );
}

function KVGrid({ children }) {
  return <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">{children}</div>;
}

function KV({ icon: Icon, label, value, valueFallback }) {
  const display = value || valueFallback || "Non renseigné";
  const isEmpty = !value;
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#F7F8F6] grid place-items-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-[#6B726A]" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#6B726A] font-medium">{label}</div>
        <div className={`text-sm mt-0.5 ${isEmpty ? "text-[#9AA09A] italic" : "text-[#0F2A1B] font-medium"}`}>{display}</div>
      </div>
    </div>
  );
}

function CheckRow({ label, on }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`w-4 h-4 rounded-md grid place-items-center ${on ? "bg-[#1A4D2E] text-white" : "bg-[#F0F2EF] text-transparent border border-[#E2E5E0]"}`}>
        <Check className="w-3 h-3" />
      </span>
      <span className={on ? "text-[#0F2A1B]" : "text-[#6B726A]"}>{label}</span>
    </div>
  );
}

function SummaryRow({ label, value, last }) {
  return (
    <div className={`py-2.5 ${last ? "" : "border-b border-dashed border-[#E2E5E0]"}`}>
      <div className="text-[11px] uppercase tracking-wider text-[#6B726A] font-medium">{label}</div>
      <div className={`text-sm mt-0.5 ${value ? "text-[#0F2A1B] font-medium" : "text-[#9AA09A] italic"}`}>{value || "Non renseigné"}</div>
    </div>
  );
}

function formatPhone(user) {
  if (!user?.phone) return null;
  const phone = String(user.phone).trim();
  if (phone.startsWith("+")) return phone;
  const code = user?.phone_country?.match(/\(([^)]+)\)/)?.[1] || "";
  return code ? `${code} ${phone}` : phone;
}
